import type { TerminalType } from 'Automations/simulation'

/**
 * Shown on the publish button once we know the rules turn this quote down. The
 * button is the only thing on screen at that point, so it has to carry the
 * reason for itself.
 */
export const PUBLISH_BUTTON_AUTO_REJECTED_TOOLTIP: string =
  'This quote does not pass the approval workflow rules, and cannot be published'

export type PublishButtonInputs = {
  /**
   * What the button says once we know, which already reads the answer: the
   * approval terminal is what makes it "Send for review" rather than "Publish".
   */
  fallbackText: string
  hasActiveApprovalAutomation: boolean
  /** We've had an answer at some point, even if we're working out a newer one. */
  simulationHasRanAtLeastOnce: boolean
  isSimulationPending: boolean
  /** The last terminal we knew. Undefined when we couldn't work one out. */
  simulatedTerminalType: TerminalType | undefined
}

export type PublishButtonState = {
  text: string
  /** Show a spinner in place of the text, because we've nothing to say yet. */
  isLoading: boolean
  isDisabled: boolean
  /** Set whenever the button is closed, so it's never closed without a reason. */
  tooltip: string | undefined
}

/**
 * What the publish button says, and the two cases where it can't be pressed.
 *
 *   nothing known yet      -> a spinner, can't press
 *   the rules turn it down -> "Publish", can't press, tooltip says why
 *   anything else          -> what it says once we know
 *
 * Only the first answer is waited for. After that the button keeps showing the
 * last one while a newer is worked out, rather than going back to the spinner
 * every time the quote is saved. Pressing it while we're working out that newer
 * answer is fine - the publish modal does the waiting from there.
 *
 * The spinner carries no tooltip. A closed button normally owes the reader a
 * reason, but a spinner is its own reason, and one on the way in would only be
 * in the way.
 */
export const publishButtonState = ({
  fallbackText,
  hasActiveApprovalAutomation,
  simulationHasRanAtLeastOnce,
  isSimulationPending,
  simulatedTerminalType
}: PublishButtonInputs): PublishButtonState => {
  const defaultState: PublishButtonState = {
    text: fallbackText,
    isLoading: false,
    isDisabled: false,
    tooltip: undefined
  }

  // With no approval workflow live there's nothing to predict, so the button
  // behaves exactly as it always has.
  if (!hasActiveApprovalAutomation) return defaultState

  if (!simulationHasRanAtLeastOnce && isSimulationPending) {
    return { ...defaultState, isLoading: true, isDisabled: true }
  }

  if (simulatedTerminalType === 'END') {
    return {
      ...defaultState,
      isDisabled: true,
      tooltip: PUBLISH_BUTTON_AUTO_REJECTED_TOOLTIP
    }
  }

  return defaultState
}
